import { setWorkspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import { CategoryPanel } from 'models/category'
import { CategoryElementListStateInfo } from 'pages/ProjectsPage/WorkspacePage'
import { ProjectElementForImport } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import * as React from 'react'
import { updatedDivisionElements } from 'shared/utils/workspaceUtils'
import {
    usePTActivationStore,
    usePTCacheStore,
} from '../features/ptActivation/store'
import {
    findNewPTNodes,
    insertPTNodesAfterParent,
    updateExistingPTNodes,
    updateParentFamilyNodes,
} from '../features/ptActivation/utils'

interface UsePTElementNodesUpdateProps {
    elementList: ProjectElementForImport[] | null
    setElementList: (list: ProjectElementForImport[] | null) => void
    selectedElementFormat: string | undefined
    isBetaWorkspaceToggleEnabled: boolean
    isOMWorkspace: boolean | undefined
    contentTeamFromStudioPage: boolean | undefined
    isFromWorkSpacePage: boolean
}

/**
 * Custom hook to watch PT activation store and update element list when PT nodes are activated.
 *
 * This hook:
 * - Watches for updated element nodes from PT activation store
 * - Updates element list in-place to reflect PT activation status changes
 * - Only runs in new workspace, AE workspace (not OM, not Studio/content team, not elements library)
 * - Clears store after processing to avoid duplicate updates
 *
 * @param props Configuration object with element list state and workspace flags
 *
 * @example
 * ```tsx
 * usePTElementNodesUpdate({
 *     elementList,
 *     setElementList,
 *     selectedElementFormat,
 *     isBetaWorkspaceToggleEnabled,
 *     isOMWorkspace,
 *     contentTeamFromStudioPage,
 *     isFromWorkSpacePage,
 * })
 * ```
 */
export const usePTElementNodesUpdate = ({
    elementList,
    setElementList,
    selectedElementFormat,
    isBetaWorkspaceToggleEnabled,
    isOMWorkspace,
    contentTeamFromStudioPage,
    isFromWorkSpacePage,
}: UsePTElementNodesUpdateProps) => {
    const updatedElementNodes = usePTActivationStore(
        (state) => state.updatedElementNodes
    )
    const setUpdatedElementNodes = usePTActivationStore(
        (state) => state.setUpdatedElementNodes
    )

    React.useEffect(() => {
        // Only run in new workspace, AE workspace (not OM, not Studio/content team, not elements library)
        const isAEWorkspace = !isOMWorkspace && !contentTeamFromStudioPage
        const shouldProcessPTActivation =
            isBetaWorkspaceToggleEnabled && isAEWorkspace && isFromWorkSpacePage // This ensures we're on workspace page, not elements library

        if (
            !shouldProcessPTActivation ||
            !updatedElementNodes ||
            updatedElementNodes.length === 0 ||
            !elementList
        ) {
            return
        }

        // IMPORTANT: Unlike AddRemoveProjectButton which adds NEW nodes to the tree,
        // PT activation only changes isInProject flag on EXISTING nodes.
        // We update ActionBarStore directly (in-place) to avoid:
        // - Calling updateElementList which triggers full tree replacement
        // - Switching back to general tab
        // - Collapsing all articles
        // - Disrupting user's current state

        const currentList = elementList

        // Step 1: Update existing PT nodes with mutation results
        let updatedList = updateExistingPTNodes(
            currentList,
            updatedElementNodes
        )

        // Step 2: Find and add new PT nodes that weren't in the list before (e.g., when filter was ON)
        const newPTs = findNewPTNodes(currentList, updatedElementNodes)

        if (newPTs.length > 0) {
            // Ensure new nodes are marked as in project
            newPTs.forEach((node) => {
                node.isInProject = true
            })

            // Step 3a: Insert each PT at the correct position among siblings.
            // The tree-building algorithm uses positional order + nesting levels.
            // Pass the cache so insertion respects server-defined PT order, not activation order.
            const cachedPTsByFamily = usePTCacheStore.getState().cachedFamilyPTs
            updatedList = insertPTNodesAfterParent(
                updatedList,
                newPTs,
                cachedPTsByFamily
            )

            // Step 3b: Update parent family nodes to mark them as having children.
            // This ensures isExpand = true (auto-expand) when tree recomputes.
            updatedList = updateParentFamilyNodes(updatedList, newPTs)
        }

        // Update ActionBarStore directly (avoids full tree replacement)
        setElementList(
            selectedElementFormat === 'DIVISIONS'
                ? updatedDivisionElements(updatedList, currentList)
                : updatedList
        )

        // Clear the store after processing (equivalent to setting mutation data to null)
        setUpdatedElementNodes(null)
    }, [
        updatedElementNodes,
        setUpdatedElementNodes,
        selectedElementFormat,
        elementList,
        setElementList,
        isBetaWorkspaceToggleEnabled,
        isOMWorkspace,
        contentTeamFromStudioPage,
        isFromWorkSpacePage,
    ])
}

interface UsePTCategoryStateUpdateProps {
    categoryElementStates: Record<string, CategoryElementListStateInfo>
    setCategoryElementStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    selectedProjectElementNode:
        | ProjectElementNodeWithMetadata
        | null
        | undefined
    setSelectedProjectElementNode: React.Dispatch<
        React.SetStateAction<ProjectElementNodeWithMetadata | null>
    >
    selectedCategory: CategoryPanel | null | undefined
}

/**
 * Custom hook to watch PT activation store and update category element states and selected node.
 *
 * This hook:
 * - Watches for updated element nodes from PT activation store
 * - Updates categoryElementStates with new PT node data for current category
 * - Updates selectedProjectElementNode if it matches an updated PT
 * - Updates workspace selected element reactive var
 *
 * @param props Configuration object with category states and selected node
 *
 * @example
 * ```tsx
 * usePTCategoryStateUpdate({
 *     categoryElementStates,
 *     setCategoryElementStates,
 *     selectedProjectElementNode,
 *     setSelectedProjectElementNode,
 *     selectedCategory,
 * })
 * ```
 */
export const usePTCategoryStateUpdate = ({
    categoryElementStates,
    setCategoryElementStates,
    selectedProjectElementNode,
    setSelectedProjectElementNode,
    selectedCategory,
}: UsePTCategoryStateUpdateProps) => {
    const updatedElementNodes = usePTActivationStore(
        (state) => state.updatedElementNodes
    )

    React.useEffect(() => {
        // Only process if we have updated nodes from PT activation
        if (!updatedElementNodes || updatedElementNodes.length === 0) {
            return
        }

        // Update categoryElementStates with new PT node data for current category
        const currentCategoryId = selectedCategory?.id ?? 'all'
        const currentCategoryState = categoryElementStates?.[currentCategoryId]

        if (currentCategoryState?.data) {
            // Step 1: Update existing nodes (with stricter matching by id for category states)
            let updatedData = updateExistingPTNodes(
                currentCategoryState.data,
                updatedElementNodes
            )

            // Step 2: Find and add new PT nodes that weren't in the category state before (e.g., when filter was ON)
            const newPTs = findNewPTNodes(
                currentCategoryState.data,
                updatedElementNodes
            )

            if (newPTs.length > 0) {
                // Insert each PT at the correct position using cache for server-defined order
                const cachedPTsByFamily = usePTCacheStore.getState()
                    .cachedFamilyPTs
                updatedData = insertPTNodesAfterParent(
                    updatedData,
                    newPTs,
                    cachedPTsByFamily
                )

                // Update parent family nodes to mark them as having children
                updatedData = updateParentFamilyNodes(updatedData, newPTs)
            }

            setCategoryElementStates({
                ...categoryElementStates,
                [currentCategoryId]: {
                    ...currentCategoryState,
                    data: updatedData,
                },
            })
        }

        // Check if currently selected node is one of the updated PT nodes
        if (selectedProjectElementNode) {
            const matchingUpdatedNode = updatedElementNodes.find(
                (updated) =>
                    updated.baseElementId ===
                        selectedProjectElementNode.baseElementId &&
                    updated.elementType === 'PRODUCTTYPE' &&
                    updated.id === selectedProjectElementNode.id
            )

            // If selected node matches updated PT, replace entire node with updated one from mutation
            if (matchingUpdatedNode) {
                setSelectedProjectElementNode(
                    matchingUpdatedNode as ProjectElementNodeWithMetadata
                )
                setWorkspaceSelectedElement(matchingUpdatedNode)
            }
        }
    }, [
        updatedElementNodes,
        selectedProjectElementNode,
        categoryElementStates,
        setCategoryElementStates,
        selectedCategory,
        setSelectedProjectElementNode,
    ])
}
